import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ScanText, Loader2, ChevronDown, ChevronUp, AlertTriangle,
  Calendar, DollarSign, Sparkles, ShieldAlert, FileSearch, Quote, RefreshCw, Share2,
} from "lucide-react";
import ShareFindingModal from "@/components/ShareFindingModal";

/**
 * DeepReadPanel — renders the forensic deep-read analysis of a document.
 *
 * Lifecycle:
 *  - status === "pending" | "processing" → animated "Compass is reading…" pill.
 *  - status === "completed" + deep_analysis present → render the structured
 *    findings (clauses to watch, red flags, deadlines, money, dates).
 *  - status === "failed" → small retry button.
 *  - status === "quota_exceeded" → upgrade prompt linking to /pricing.
 *  - Legacy docs with no status at all → "Read this in full" button to trigger
 *    the backfill.
 *
 * Deliberately separate from DocSummary (the older on-demand one-paragraph
 * summary). The deep read is the headline product moment — finding the small
 * print agents bury in special terms — so it earns prime visual real estate.
 */
export default function DeepReadPanel({ doc, onUpdate }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const status = doc.deep_analysis_status || (doc.deep_analysis ? "completed" : null);
  const a = doc.deep_analysis;

  const trigger = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/deep-read`);
      toast.success("Compass is reading this document. Refreshing in a moment.");
      onUpdate?.({ ...doc, deep_analysis_status: "pending" });
      // Light poll - parent reload will pick up final state.
      setTimeout(() => onUpdate?.(), 4000);
    } catch (e) {
      const detail = e.response?.data?.detail || "Couldn't start deep read.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!status && !a) {
    // Legacy / pre-feature document — offer to run it now.
    return (
      <div className="mt-3 p-3 bg-[#FAF9F6] border-l-2 border-[#1B6C62] flex items-start justify-between gap-3 flex-wrap" data-testid={`deepread-legacy-${doc.id}`}>
        <div className="flex items-start gap-2 max-w-md">
          <ScanText size={14} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5 flex-shrink-0" />
          <div className="text-[12px] text-[#0D2B52] font-serif leading-snug">
            <strong>This document hasn&apos;t been deep-read yet.</strong> Have Compass scan it cover to cover, catch every clause, flag the small print agents hide in special terms.
          </div>
        </div>
        <button
          onClick={trigger}
          disabled={busy}
          className="btn-primary text-[11px] inline-flex items-center gap-1.5 whitespace-nowrap"
          data-testid={`deepread-run-${doc.id}`}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <ScanText size={11} strokeWidth={1.75} />}
          Read this in full
        </button>
      </div>
    );
  }

  if (status === "pending" || status === "processing") {
    return (
      <div className="mt-3 p-3 bg-[#FAF9F6] border border-[#E5E4DE] inline-flex items-center gap-2 text-[12px] font-serif text-[#0D2B52]" data-testid={`deepread-progress-${doc.id}`}>
        <Loader2 size={12} strokeWidth={1.75} className="animate-spin text-[#1B6C62]" />
        Compass is reading this document in full. This usually takes 20 to 60 seconds.
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="mt-3 p-3 bg-[#FAF6E8] border border-[#C9A961]/40 flex items-center justify-between gap-3 flex-wrap" data-testid={`deepread-failed-${doc.id}`}>
        <div className="text-[12px] text-[#8A6D3B] font-serif">Compass couldn&apos;t finish reading this document. Try again?</div>
        <button onClick={trigger} disabled={busy} className="text-[11px] inline-flex items-center gap-1.5 text-[#1B6C62] hover:underline" data-testid={`deepread-retry-${doc.id}`}>
          <RefreshCw size={11} strokeWidth={1.75} /> Retry
        </button>
      </div>
    );
  }

  if (status === "quota_exceeded" || (a && a._quota_exceeded)) {
    return (
      <div className="mt-3 p-4 bg-[#FAF7F0] border-l-2 border-[#1B6C62] flex items-start justify-between gap-3 flex-wrap" data-testid={`deepread-quota-${doc.id}`}>
        <div className="flex items-start gap-2 max-w-md">
          <Sparkles size={14} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5" />
          <div className="text-[13px] text-[#0D2B52] font-serif leading-snug">
            <strong>Deep-read limit reached on your current plan.</strong> Upgrade to Shield so every document you upload gets read in full, every clause flagged, every gotcha caught.
          </div>
        </div>
        <Link to="/pricing" className="btn-primary text-[11px] whitespace-nowrap" data-testid={`deepread-upgrade-${doc.id}`}>See plans</Link>
      </div>
    );
  }

  if (!a) return null;

  const hasRedFlags = Array.isArray(a.red_flags) && a.red_flags.length > 0;
  const hasClauses = Array.isArray(a.clauses_to_watch) && a.clauses_to_watch.length > 0;
  const hasUnusual = Array.isArray(a.unusual_terms) && a.unusual_terms.length > 0;
  const hasDates = Array.isArray(a.key_dates) && a.key_dates.length > 0;
  const hasMoney = Array.isArray(a.money_amounts) && a.money_amounts.length > 0;
  const hasDeadlines = Array.isArray(a.deadlines_for_action) && a.deadlines_for_action.length > 0;
  const hasMissing = Array.isArray(a.missing_items) && a.missing_items.length > 0;

  // Headline count of "things worth knowing" — the upsell hook on the collapsed bar.
  const flagCount = (a.red_flags?.length || 0) + (a.clauses_to_watch?.length || 0) + (a.unusual_terms?.length || 0);

  return (
    <div className="mt-3 bg-white border border-[#1B6C62]/40 shadow-sm" data-testid={`deepread-${doc.id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-[#FAF9F6] transition-colors"
        data-testid={`deepread-toggle-${doc.id}`}
      >
        <span className="inline-flex items-center gap-2 text-[13px] text-[#0D2B52] font-medium">
          <FileSearch size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
          Compass&apos;s deep read
          {flagCount > 0 && (
            <span className="font-mono text-[10px] tracking-wider uppercase text-[#B35D4C] bg-[#FAF6E8] border border-[#C9A961]/40 px-1.5 py-0.5">
              {flagCount} thing{flagCount === 1 ? "" : "s"} worth knowing
            </span>
          )}
        </span>
        {open ? <ChevronUp size={14} strokeWidth={1.75} /> : <ChevronDown size={14} strokeWidth={1.75} />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-[#1B6C62]/20" data-testid={`deepread-body-${doc.id}`}>
          {a.overview && (
            <p className="text-[13px] text-[#1A1A1A] leading-relaxed font-serif">{a.overview}</p>
          )}

          {/* Red flags — top priority */}
          {hasRedFlags && (
            <div data-testid={`deepread-redflags-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#B35D4C] mb-1.5 flex items-center gap-1">
                <ShieldAlert size={11} strokeWidth={1.75} /> Red flags
              </div>
              <ul className="space-y-1 text-[13px] text-[#1A1A1A] font-serif">
                {a.red_flags.map((r, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#B35D4C]">•</span><span>{r}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* Clauses to watch — the meat */}
          {hasClauses && (
            <div data-testid={`deepread-clauses-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#8A6D3B] mb-1.5 flex items-center gap-1">
                <Quote size={11} strokeWidth={1.75} /> Clauses to watch
              </div>
              <ul className="space-y-3">
                {a.clauses_to_watch.map((c, i) => (
                  <li key={i} className="bg-[#FAF6E8] border-l-2 border-[#C9A961] px-3 py-2">
                    {c.clause && <div className="font-mono text-[10px] text-[#8A6D3B] uppercase tracking-wider mb-1">{c.clause}</div>}
                    {c.verbatim_quote && <blockquote className="text-[12.5px] text-[#1A1A1A] italic font-serif mb-1.5">{`"${c.verbatim_quote}"`}</blockquote>}
                    {c.why_it_matters && <p className="text-[12.5px] text-[#0D2B52] font-serif leading-snug">{c.why_it_matters}</p>}
                    {c.act_section_potentially_breached && (
                      <p className="text-[11px] text-[#1B6C62] font-mono mt-1">Possibly conflicts with: {c.act_section_potentially_breached}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unusual terms */}
          {hasUnusual && (
            <div data-testid={`deepread-unusual-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#8A6D3B] mb-1.5 flex items-center gap-1">
                <AlertTriangle size={11} strokeWidth={1.75} /> Unusual terms
              </div>
              <ul className="space-y-1 text-[12.5px] text-[#1A1A1A] font-serif list-disc list-inside marker:text-[#8A6D3B]">
                {a.unusual_terms.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}

          {/* Deadlines for action */}
          {hasDeadlines && (
            <div data-testid={`deepread-deadlines-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#B35D4C] mb-1.5 flex items-center gap-1">
                <Calendar size={11} strokeWidth={1.75} /> Deadlines you can&apos;t miss
              </div>
              <ul className="space-y-1.5 text-[12.5px] text-[#1A1A1A] font-serif">
                {a.deadlines_for_action.map((d, i) => (
                  <li key={i} className="border-l-2 border-[#B35D4C] pl-2.5">
                    <span className="font-mono text-[#0D2B52]">{d.by}</span>, {d.action}
                    {d.consequence && <div className="text-[11px] text-[#6B7280] italic">If missed: {d.consequence}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key dates and money in a two-column grid */}
          {(hasDates || hasMoney) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {hasDates && (
                <div data-testid={`deepread-dates-${doc.id}`}>
                  <div className="label-mono text-[10px] text-[#1B6C62] mb-1.5 flex items-center gap-1">
                    <Calendar size={11} strokeWidth={1.75} /> Key dates
                  </div>
                  <ul className="space-y-1 text-[12px] text-[#1A1A1A] font-serif">
                    {a.key_dates.map((k, i) => (
                      <li key={i}><span className="font-mono text-[#0D2B52]">{k.date}</span>, {k.what}</li>
                    ))}
                  </ul>
                </div>
              )}
              {hasMoney && (
                <div data-testid={`deepread-money-${doc.id}`}>
                  <div className="label-mono text-[10px] text-[#1B6C62] mb-1.5 flex items-center gap-1">
                    <DollarSign size={11} strokeWidth={1.75} /> Money mentioned
                  </div>
                  <ul className="space-y-1 text-[12px] text-[#1A1A1A] font-serif">
                    {a.money_amounts.map((m, i) => (
                      <li key={i}><span className="font-mono text-[#0D2B52]">{m.amount}</span>, {m.what}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Missing items */}
          {hasMissing && (
            <div data-testid={`deepread-missing-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#8A6D3B] mb-1.5 flex items-center gap-1">
                <AlertTriangle size={11} strokeWidth={1.75} /> Possibly missing
              </div>
              <ul className="space-y-1 text-[12.5px] text-[#1A1A1A] font-serif list-disc list-inside marker:text-[#8A6D3B]">
                {a.missing_items.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="pt-2 border-t border-[#1B6C62]/20 flex items-center justify-between text-[10px] font-mono text-[#6B7280] flex-wrap gap-2">
            <span>Read in full by Compass · {doc.deep_analysis_at ? new Date(doc.deep_analysis_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1 text-[10px] text-[#1B6C62] hover:underline font-mono uppercase tracking-wider"
              data-testid={`deepread-share-${doc.id}`}
            >
              <Share2 size={10} strokeWidth={1.75} /> Share this report
            </button>
          </div>
        </div>
      )}
      <ShareFindingModal doc={doc} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}
